import React from "react";
import { ArrowRight } from "lucide-react";
import { AppHeader } from "../../../../components/layout/AppHeader";
import { LandingFooter } from "../landing/LandingFooter";
import { useLandingScrollToSection } from "../marketing/useLandingScrollToSection";
import { FadeUp } from "../landing/FadeUp";
import type { BlogPostCard, RouteMap, UserDisplay } from "../../types";

type BlogIndexPageProps = {
  posts: BlogPostCard[];
  loggedIn: boolean;
  hasBusiness: boolean;
  routes: RouteMap;
  csrfToken: string;
  page: string;
  userDisplay?: UserDisplay | null;
};

const BlogCard: React.FC<{ post: BlogPostCard }> = ({ post }) => (
  <a
    href={post.href}
    aria-label={`Read article: ${post.title}`}
    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all duration-200 ease-out hover:-translate-y-1 hover:border-premium-300 hover:shadow-lg cursor-pointer no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
  >
    <article className="flex h-full flex-col">
      {post.heroImageUrl ? (
        <div className="aspect-video overflow-hidden bg-muted">
          <img
            src={post.heroImageUrl}
            alt={post.heroImageAlt ?? post.title}
            className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
            loading="lazy"
            decoding="async"
          />
        </div>
      ) : null}
      <div className="flex flex-1 flex-col gap-4 p-6">
        {post.tradeTags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {post.tradeTags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-full bg-premium-50 px-2.5 py-0.5 text-xs font-semibold text-premium-700"
              >
                {tag.replace(/-/g, " ")}
              </span>
            ))}
          </div>
        ) : null}
        <h2 className="font-display text-xl font-bold text-foreground transition-colors group-hover:text-premium-600">
          {post.title}
        </h2>
        {post.excerptHtml ? (
          <div
            className="text-sm leading-relaxed text-muted-foreground line-clamp-3"
            dangerouslySetInnerHTML={{ __html: post.excerptHtml }}
          />
        ) : null}
        <div className="mt-auto flex items-center justify-between text-sm text-muted-foreground">
          <span>{post.authorName}</span>
          {post.publishedAtDisplay ? (
            <time dateTime={post.publishedAt ?? undefined}>
              {post.publishedAtDisplay}
            </time>
          ) : null}
        </div>
        <span className="inline-flex items-center text-sm font-semibold text-premium-600">
          Read article
          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </article>
  </a>
);

export const BlogIndexPage: React.FC<BlogIndexPageProps> = ({
  posts,
  loggedIn,
  hasBusiness,
  routes,
  csrfToken,
  page,
  userDisplay,
}) => {
  const scrollToSection = useLandingScrollToSection();

  return (
    <div className="min-h-screen overflow-x-hidden bg-background text-foreground">
      <AppHeader
        variant="app"
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        routes={routes}
        csrfToken={csrfToken}
        page={page}
        userDisplay={userDisplay}
      />
      <section className="bg-background pt-20 pb-10 sm:pt-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <span className="inline-flex items-center rounded-full border border-premium-500/40 bg-premium-50 px-3 py-1 text-sm font-semibold text-premium-700">
              ToolKit Blog
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
              Local search, quote forms, and contractor websites
            </h1>
            <p className="mt-5 max-w-3xl text-lg text-muted-foreground">
              Short reads for plumbers, electricians, HVAC techs, and other
              independent trades. How to get found nearby and take quote
              requests without a lead mill.
            </p>
          </FadeUp>
        </div>
      </section>
      <section className="bg-card py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {posts.length === 0 ? (
            <p className="text-center text-muted-foreground">
              No articles published yet. Check back soon.
            </p>
          ) : (
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>
          )}
        </div>
      </section>
      <LandingFooter
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        routes={routes}
        scrollToSection={scrollToSection}
      />
    </div>
  );
};
